import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ExternalLink, PanelBottom, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import {
  useDetachedWindow,
  type DetachedWindowHandlers,
} from './useDetachedWindow'
import { JanelaDoPainelContext } from './janelaDoPainel'
import {
  avisoJanelaBloqueada,
  escapeQueFechaPainel,
  FEATURES_PAINEL,
} from './abrirJanela'

interface JanelaDestacadaProps {
  /** Destacar agora. Com false não se abre nada (e fecha-se a que
   *  estiver aberta). */
  aberta: boolean
  /** Nome da janela do browser (único por painel, ver
   *  `abrirJanela.ts`). */
  nome: string
  /** Título da janela e da barra no topo. */
  titulo: string
  features?: string
  /** O painel volta ao sítio: a janela foi fechada, bloqueada, ou a
   *  utilizadora carregou em "Voltar ao painel". */
  aoVoltar: () => void
  /** Fechar o painel de vez (botão × na barra). Sem isto não há ×. */
  aoFechar?: () => void
  /** Para o aviso de bloqueio: "a Section box", "o tópico", ... */
  oQue?: string
  /** Escape na janela destacada (fora de campos de texto). Os atalhos
   *  da aplicação só ouvem a janela principal. */
  aoEscape?: () => void
  children: ReactNode
}

/**
 * Um painel inteiro numa janela do browser à parte.
 *
 * Quem usa decide quando: com `aberta`, mostra isto em vez do painel no
 * sítio de sempre (e devolve null no outro ramo). Ao fechar a janela,
 * ao ser bloqueada, ou com "Voltar ao painel", chama `aoVoltar` e o
 * painel volta a aparecer onde estava.
 *
 * Os filhos podem saber em que janela estão com `useJanelaDoPainel()`
 * (listeners de teclado e de documento têm de ir para lá).
 */
export default function JanelaDestacada({
  aberta,
  nome,
  titulo,
  features = FEATURES_PAINEL,
  aoVoltar,
  aoFechar,
  oQue = 'o painel',
  aoEscape,
  children,
}: JanelaDestacadaProps) {
  const pushToast = useAppStore((s) => s.pushToast)
  const handlers = useMemo<DetachedWindowHandlers>(
    () => ({
      onBlocked: () => {
        aoVoltar()
        pushToast(avisoJanelaBloqueada(oQue), { type: 'error' })
      },
      onClose: aoVoltar,
    }),
    [aoVoltar, pushToast, oQue],
  )
  const { win, container } = useDetachedWindow(aberta, handlers, {
    name: nome,
    title: `VN2R openBIM Hub · ${titulo}`,
    features,
  })

  // O teclado da janela destacada é dela: o `window` da aplicação é o da
  // janela principal, e um listener lá não ouve nada do que se carrega
  // aqui.
  const aoEscapeRef = useRef(aoEscape)
  useEffect(() => {
    aoEscapeRef.current = aoEscape
  })
  useEffect(() => {
    if (!win) return
    const onKey = (e: KeyboardEvent) => {
      if (!aoEscapeRef.current || !escapeQueFechaPainel(e)) return
      e.preventDefault()
      aoEscapeRef.current()
    }
    win.addEventListener('keydown', onKey)
    return () => win.removeEventListener('keydown', onKey)
  }, [win])

  if (!aberta || !container || !win) return null

  return createPortal(
    <JanelaDoPainelContext.Provider value={win}>
      <div
        data-testid="janela-destacada-barra"
        className="flex h-8 shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs"
      >
        <span className="min-w-0 flex-1 truncate text-[var(--color-text-muted)]">
          {titulo} (janela destacada)
        </span>
        <button
          type="button"
          onClick={aoVoltar}
          title="Voltar a mostrar o painel na janela principal"
          className="flex shrink-0 items-center gap-1 rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          <PanelBottom size={11} />
          Voltar ao painel
        </button>
        {aoFechar && (
          <button
            type="button"
            onClick={aoFechar}
            title="Fechar"
            aria-label={`Fechar ${titulo}`}
            className="shrink-0 rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <X size={13} />
          </button>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto bg-[var(--color-bg)] text-[var(--color-text)]">
        {children}
      </div>
    </JanelaDoPainelContext.Provider>,
    container,
  )
}

/** O botão "destacar" das barras de título, igual em todos os painéis. */
export function BotaoDestacar({
  onClick,
  rotulo,
  tamanho = 12,
  className = 'rounded p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
}: {
  onClick: () => void
  /** Para o leitor de ecrã: "Destacar X para uma janela do browser". */
  rotulo: string
  tamanho?: number
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Abrir numa janela do browser (por exemplo, num segundo ecrã)"
      aria-label={rotulo}
      className={className}
    >
      <ExternalLink size={tamanho} />
    </button>
  )
}
